/**
 * Web push subscription lifecycle for the PWA.
 *
 * Nothing here runs unless the user explicitly asks for push in Settings, so
 * we never silently prompt on page load — browsers penalise sites that do,
 * and iOS just refuses.
 *
 * iOS caveat: web push on iOS only works when the app has been added to the
 * Home Screen (iOS 16.4+). We detect the un-installed case and bail with a
 * friendly message rather than firing PushManager.subscribe() and failing
 * with an opaque DOMException.
 */

import supabase from "../config/supabase";
import pushSubscriptionsApi from "../api/pushSubscriptions";
import notificationsApi from "../api/notifications";

const PUBLIC_KEY_CACHE_KEY = "oe.vapidPublicKey.v1";

// PushManager expects the raw bytes of the VAPID public key, not the base64url
// string the server hands us.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function arrayBufferToBase64(buffer) {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Exported because the install guide has to tell a member the right thing for
// the phone in their hand, and getting it wrong — telling someone who already
// installed the app to go and install it — is worse than saying nothing.
export function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/**
 * What to call this device in the confirmation. The alert goes to every phone
 * the member has switched alerts on for, so the one that just did it needs to
 * be named, or the other phones show a test message nobody there asked for.
 */
export function deviceLabel() {
  if (isIos()) return "iPhone";
  if (isAndroid()) return "Android phone";
  return "browser";
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent);
}

export function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return (mm && mm.matches) || window.navigator.standalone === true;
}

/**
 * Whether the member has already refused the permission prompt.
 *
 * Worth its own helper because iOS makes a refusal **sticky** — the prompt never
 * appears again, `subscribeToPush()` throws "Notifications are blocked", and
 * there is nothing the app can do about it. The only way out is the phone's own
 * Settings, so the guide has to say so. Bernard hit exactly this on 6 Oct and it
 * cost an hour.
 */
export function notificationsBlocked() {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  return Notification.permission === "denied";
}

export function isLocalPushDisabled() {
  return process.env.NODE_ENV === "development";
}

export function pushSupported() {
  if (isLocalPushDisabled()) return false;
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  if (!("Notification" in window)) return false;
  // iOS Safari exposes the APIs even outside a Home Screen install, but
  // PushManager.subscribe() just throws. Report unsupported until installed.
  if (isIos() && !isStandalonePwa()) return false;
  return true;
}

export function pushUnsupportedReason() {
  if (isLocalPushDisabled()) {
    return "Alerts aren't available in this local preview. Open outbackexchange.ai to turn them on for this device.";
  }
  if (typeof window === "undefined") return "Not available in this environment.";
  if (isIos() && !isStandalonePwa()) {
    return "On iPhone, tap Share and then Add to Home Screen first.";
  }
  if (!("serviceWorker" in navigator)) return "This browser can't run a service worker.";
  if (!("PushManager" in window)) return "This browser doesn't support push notifications.";
  if (!("Notification" in window)) return "This browser doesn't support notifications.";
  return "";
}

async function getPublicKey() {
  const cached = window.localStorage.getItem(PUBLIC_KEY_CACHE_KEY);
  if (cached) return cached;

  const { data, error } = await supabase.functions.invoke("push-public-key");
  if (error || !data?.key) {
    throw new Error("Push notifications aren't set up on the server yet.");
  }
  window.localStorage.setItem(PUBLIC_KEY_CACHE_KEY, data.key);
  return data.key;
}

async function readyRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  let timer;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(
          "Alerts couldn't get ready. Reload the app and try again."
        )), 10000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch and cache everything subscribeToPush() would otherwise have to wait
 * for, so the subscribe call itself can run in the same breath as the tap.
 *
 * iOS only permits PushManager.subscribe() while it still considers the page
 * to be handling a user gesture. A round trip for the VAPID key is enough to
 * lose that, which made the very first toggle fail for everyone and work on
 * the second try. Call this when the screen with the toggle mounts.
 */
export async function prewarmPush() {
  if (!pushSupported()) return;
  try {
    await Promise.all([readyRegistration(), getPublicKey()]);
  } catch {
    // Nothing to report: the toggle re-runs these and surfaces the real error.
  }
}

export async function isPushSubscribed() {
  if (!pushSupported()) return false;
  if (Notification.permission !== "granted") return false;
  const reg = await readyRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

/**
 * Ask the browser for permission, get a PushSubscription, and mirror it to
 * push_subscriptions so the server can send to it.
 *
 * Throws with a user-facing string on any refusal or misconfiguration; the
 * caller shows that in the UI without translation.
 */
export async function subscribeToPush() {
  if (!pushSupported()) {
    throw new Error(pushUnsupportedReason());
  }

  if (Notification.permission === "denied") {
    throw new Error("Notifications are blocked in your browser settings.");
  }

  // Everything slow happens before the permission prompt, so that once
  // permission lands we can call subscribe() with nothing awaited in between.
  // iOS refuses subscribe() once it stops counting the page as handling the
  // tap, and a key fetch in the middle is enough to cross that line.
  const reg = await readyRegistration();
  if (!reg) throw new Error("Service worker isn't ready. Try again in a moment.");

  const publicKey = await getPublicKey();
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  let subscription = await reg.pushManager.getSubscription();

  if (Notification.permission !== "granted") {
    const outcome = await Notification.requestPermission();
    if (outcome !== "granted") throw new Error("Notifications weren't allowed.");
  }

  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  const p256dh = arrayBufferToBase64(subscription.getKey("p256dh"));
  const auth = arrayBufferToBase64(subscription.getKey("auth"));

  const result = await pushSubscriptionsApi.upsert({
    endpoint: subscription.endpoint,
    p256dh,
    auth,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  });
  if (!result.success) {
    // Roll back the browser subscription so the toggle can retry cleanly.
    await subscription.unsubscribe().catch(() => {});
    throw new Error(result.error || "Couldn't save your push subscription.");
  }

  // Prove it works while they're still looking at the phone. Deliberately not
  // awaited for success: the subscription is already saved, and a failure to
  // write the confirmation row must not make a working setup report as broken.
  notificationsApi.confirmPushEnabled(deviceLabel()).catch(() => {});

  return true;
}

export async function unsubscribeFromPush() {
  if (typeof navigator === "undefined") return true;
  if (!("serviceWorker" in navigator)) return true;

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return true;

  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  await pushSubscriptionsApi.removeByEndpoint(sub.endpoint);
  await sub.unsubscribe().catch(() => {});
  return true;
}
